import { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { sanitizeEmail } from "../utils/sanitization";
import { validateEmail } from "../utils/validation";

export const useForgotPasswordForm = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [lastRequestTime, setLastRequestTime] = useState(0);
  const [captchaToken, setCaptchaToken] = useState("");
  const RATE_LIMIT_WINDOW = 60000; // 1 minute
  const RESEND_COOLDOWN = 60; // 60 seconds

  useEffect(() => {
    let timer;
    if (resendCountdown > 0) {
      timer = setInterval(() => {
        setResendCountdown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendCountdown]);

  const handleChange = (e) => {
    const sanitizedValue = sanitizeEmail(e.target.value);
    setEmail(sanitizedValue);
    setError(null);
  };

  const handleCaptchaError = () => {
    setCaptchaToken("");
    setError("Captcha verification failed. Please try again.");
  };

  const handleCaptchaExpire = () => {
    setCaptchaToken("");
  };

  const handleCaptchaVerify = (token) => {
    setCaptchaToken(token);
    setError(null); // Simply clear the error when CAPTCHA is verified
  };

  const validateAndSubmit = async () => {
    // Validate email
    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      setError(emailValidation.error);
      return false;
    }

    // Validate CAPTCHA
    if (!captchaToken) {
      setError("Please complete the captcha verification");
      return false;
    }

    // Check rate limiting
    const now = Date.now();
    if (now - lastRequestTime < RATE_LIMIT_WINDOW) {
      setError(
        "Please wait a moment before requesting another password reset."
      );
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
        options: { captchaToken: captchaToken },
      });

      if (error) {
        console.error("Password reset error:", error);
        // Sanitize error message for user display
        if (error.message.includes("rate limit")) {
          setError("Too many requests. Please try again later.");
        } else if (error.message.includes("not found")) {
          setError("No account found with this email address.");
        } else {
          setError("Failed to send reset instructions. Please try again.");
        }
        setSuccess(false);
        return false;
      }

      setSuccess(true);
      setError(null);
      setLastRequestTime(now);
      setResendCountdown(RESEND_COOLDOWN);
      return true;
    } catch (err) {
      console.error("Unexpected error:", err);
      setError("An unexpected error occurred. Please try again.");
      setSuccess(false);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await validateAndSubmit();
  };

  const handleResend = async () => {
    if (resendCountdown > 0) {
      setError(
        `Please wait ${resendCountdown} seconds before requesting another reset.`
      );
      return;
    }
    await validateAndSubmit();
  };

  return {
    email,
    loading,
    success,
    error,
    resendCountdown,
    captchaToken,
    handleChange,
    handleSubmit,
    handleResend,
    handleCaptchaError,
    handleCaptchaExpire,
    handleCaptchaVerify,
  };
};
